import { useCallback, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthModal } from "@/components/ui/auth-modal";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { UnsupportedFileModal, checkImageFile, unsupportedReasonFromError, IMAGE_ACCEPT, type UnsupportedReason } from "@/components/ui/unsupported-file-modal";
import { useGenerate } from "@/hooks/use-generation";
import { api } from "@/lib/api";
import { useUserStore } from "@/stores/user-store";
import { INTEGRATED_MODEL_ID, POWER_TIER_LABEL } from "@/config/models";
import { ArrowRight, Gift, ImagePlus, Loader2, Sparkles, Volume2, X } from "lucide-react";

const PROMO_ID = "free_h3_15s";
const ANIMATE_HREF = "/create-cartoon";

interface PromoStatus {
  success: boolean;
  active: boolean;
  eligible: boolean;
  used: boolean;
  has_purchased?: boolean;
  reason: string | null;
  model_id: string;
  duration_seconds: number;
}

export function useFreeH3PromoStatus(userId?: string | null) {
  return useQuery({
    queryKey: ["promo", "free-h3-15s", userId ?? "anon"],
    queryFn: () => api.get<PromoStatus>("/promo/free-h3-15s"),
    staleTime: 30_000,
  });
}

export function FreeGenPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isSignedIn } = useUser();
  const { hasPurchased } = useUserStore();
  const { data: promo, isLoading } = useFreeH3PromoStatus(user?.id);
  const generateMutation = useGenerate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageUrl, setImageUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [unsupported, setUnsupported] = useState<UnsupportedReason | null>(null);
  const [showAuth, setShowAuth] = useState(false);

  const canGenerate = !!imageUrl && prompt.trim().length > 0;

  const handleUpload = useCallback(async (file: File) => {
    if (!user?.id) {
      setShowAuth(true);
      return;
    }
    const unsupportedReason = checkImageFile(file);
    if (unsupportedReason) { setUnsupported(unsupportedReason); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.upload<{ success: boolean; url: string }>("/upload", formData);
      if (res.url) setImageUrl(res.url);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  }, [user?.id]);

  const handleGenerate = useCallback(async () => {
    if (!imageUrl || !prompt.trim()) return;
    try {
      const result = await generateMutation.mutateAsync({
        model_id: INTEGRATED_MODEL_ID,
        prompt: prompt.trim(),
        reference_image: imageUrl,
        duration_seconds: 15,
        promo: PROMO_ID,
      });
      await queryClient.invalidateQueries({ queryKey: ["promo", "free-h3-15s"] });
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) {
      setUnsupported(unsupportedReasonFromError(err));
      console.error("Free generation failed:", err);
    }
  }, [imageUrl, prompt, generateMutation, navigate, queryClient]);

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-5 py-16 flex justify-center">
        <Loader2 className="size-6 animate-spin text-[#7c5cf0]" />
      </div>
    );
  }

  if (!promo?.active || promo.used || (isSignedIn && hasPurchased && !promo.eligible && promo.reason === "already_used")) {
    return <Navigate to="/" replace />;
  }

  if (isSignedIn && !hasPurchased) {
    return (
      <div className="max-w-lg mx-auto px-5 py-12 text-center space-y-4">
        <Gift className="size-10 text-[#7c5cf0] mx-auto" />
        <h1 className="text-2xl font-extrabold text-[#241d3a]">Purchasers only</h1>
        <p className="text-[14px] text-[#241d3a]/60 leading-relaxed font-semibold">
          This limited-time free 15s {POWER_TIER_LABEL} generation is for anyone who&apos;s bought a token pack.
        </p>
        <Link to="/billing" className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-xl text-[14px]">
          <Sparkles className="size-4" />
          Get Tokens
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 py-8 space-y-6">
      <div className="rounded-2xl border-2 border-[#241d3a]/15 bg-[#b8a5f0]/20 p-5 space-y-2 shadow-[3px_3px_0_rgba(42,35,66,0.12)]">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#7c5cf0] text-white text-[10px] font-extrabold uppercase tracking-wider">
          <Gift className="size-3" />
          Limited time
        </div>
        <h1 className="text-2xl font-extrabold text-[#241d3a] tracking-tight">
          Free Generation — 15 Second
        </h1>
        <p className="text-[13px] text-[#241d3a]/60 leading-relaxed font-semibold">
          One free {POWER_TIER_LABEL} video with native audio for purchasers. Upload an image, write a prompt, and go — 0 tokens.
        </p>
        <p className="text-[12px] text-[#241d3a] font-bold flex items-center gap-1.5 pt-1">
          <Volume2 className="size-3.5 text-[#7c5cf0]" />
          Locked to {POWER_TIER_LABEL} · 15s · with sound
        </p>
      </div>

      <div>
        {imageUrl ? (
          <div className="relative rounded-2xl overflow-hidden border-2 border-[#241d3a]/15 aspect-video bg-[#241d3a]/5">
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => setImageUrl("")}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white"
            >
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (!isSignedIn) setShowAuth(true);
              else fileInputRef.current?.click();
            }}
            disabled={uploading}
            className="w-full aspect-video rounded-2xl border-2 border-dashed border-[#241d3a]/25 bg-[#241d3a]/[0.04] flex flex-col items-center justify-center gap-2 hover:border-[#7c5cf0]/50 transition-colors"
          >
            {uploading ? (
              <Loader2 className="size-6 animate-spin text-[#7c5cf0]" />
            ) : (
              <>
                <ImagePlus className="size-7 text-[#241d3a]/40" />
                <span className="text-[13px] font-bold text-[#241d3a]/55">Upload starting image</span>
              </>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
          }}
        />
      </div>

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="The character starts walking forward, turns and winks, colorful background..."
        rows={3}
        className="input-field resize-none"
      />

      <Show when="signed-out">
        <button
          type="button"
          onClick={() => setShowAuth(true)}
          className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
        >
          <Sparkles className="size-4" />
          Sign Up to Claim
        </button>
      <UnsupportedFileModal reason={unsupported} onClose={() => setUnsupported(null)} onChooseAnother={() => fileInputRef.current?.click()} />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} title="Sign Up to Claim Free Gen" />
      </Show>

      <Show when="signed-in">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate || generateMutation.isPending}
          className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
        >
          {generateMutation.isPending ? (
            <><Loader2 className="size-4 animate-spin" />Generating free 15s...</>
          ) : (
            <><Gift className="size-4" />Generate Free 15s Video</>
          )}
        </button>
      </Show>

      {generateMutation.isError && <NsfwOrError error={generateMutation.error} />}

      <div className="rounded-xl border-2 border-[#241d3a]/10 bg-white/70 p-4 text-center space-y-2">
        <p className="text-[13px] text-[#241d3a]/60 leading-relaxed font-semibold">
          Want more {POWER_TIER_LABEL} 15s videos? Generate them anytime on the Generate Video page (uses tokens).
        </p>
        <Link to={ANIMATE_HREF} className="inline-flex items-center gap-1.5 text-[13px] font-extrabold text-[#7c5cf0] hover:underline">
          Go to Generate Video
          <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
